import { Typography, type TypographyProps } from '@mui/material';

import type { DrugExpiryDateSettings } from '@/api/domains/settings.types';
import { isExpirationDateClose } from '@/shared/helpers/date.helpers';
import { formatDateTime } from '@/shared/helpers/format.helpers';
import { useDrugExpiryDateSettings } from '@/shared/hooks/useDrugExpiryDateSettings';

export type ExpirationDateType = 'colorful' | 'default';

type ExpirationDateColorConfig = {
  type: ExpirationDateType;
  expiryDate: Date;
  defaultColor: TypographyProps['color'];
  drugExpiryDateSettings?: DrugExpiryDateSettings;
};

const getExpirationDateColor = (config: ExpirationDateColorConfig) => {
  const { expiryDate, type, defaultColor, drugExpiryDateSettings } = config;

  if (type === 'default' || !drugExpiryDateSettings) {
    return defaultColor;
  }

  const { warning, danger } = drugExpiryDateSettings;

  if (danger.is_active && isExpirationDateClose(expiryDate, danger.days)) {
    return 'error.main';
  }

  if (warning.is_active && isExpirationDateClose(expiryDate, warning.days)) {
    return 'warning.main';
  }

  return defaultColor;
};

export type DrugExpiryDateProps = Omit<TypographyProps, 'component'> & {
  type?: ExpirationDateType;
  dateFormat?: string;
  value: string | number;
};

export const DrugExpiryDate = (props: DrugExpiryDateProps) => {
  const {
    type: passedType = 'default',
    color: defaultColor = 'text.primary',
    dateFormat = 'dd.MM.yyyy',
    value,
    ...restOfTypographyProps
  } = props;

  const { isLoading, data: drugExpiryDateSettings } = useDrugExpiryDateSettings();
  const type = isLoading ? 'default' : passedType;

  const typographyColor = getExpirationDateColor({
    type,
    defaultColor,
    expiryDate: new Date(value),
    drugExpiryDateSettings,
  });

  return (
    <Typography
      noWrap
      variant="Body/medium"
      color={typographyColor}
      data-testid="drug-expiry-cell"
      {...restOfTypographyProps}
    >
      {formatDateTime(value, dateFormat)}
    </Typography>
  );
};
